import React from "react";
import { autoFontColor } from "../../../../utils/color";
import { formatDateMonthDay } from "../../../../utils/format";
import { constraintInfo } from "../../../../utils/p6";
import type { DepGraph, DepGraphActivity } from "../../../../models";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import {
  activityLabel,
  driverDate,
  driverDescriptor,
  driverWindow,
  DRIVER_STRIP_H,
  nodeStatus,
  ROW_H,
  type RowDriver,
  type RowLayoutEntry,
} from "../graphRows";
import DepArrows from "./DepArrows/DepArrows";
import { computeArrows, type ActivityBox } from "./computeArrows";
import { fmtMonthDay } from "../../utils/dateFormatters";
import "./DepGanttStrip.css";

/** Column width per workday. */
const DAY_PX = 20;
const BAR_H = 14;
// Min px to the left of a bar/milestone for the constraint chip to fit before
// it; below this the chip flips to after the bar so it never clips off-screen.
const CSTR_CHIP_ROOM = 90;
const DRIVER_H = 11;

// Rough px width of a bar label (≈11px font) — decides whether the label fits
// INSIDE the bar or must overflow to the RIGHT (the one consistent ext-label
// pattern, used by both section bars and driver markers).
const fitsInBar = (text: string, barWidth: number): boolean =>
  text.trim().length * 6.6 + 18 <= barWidth;

/** One drawn segment of a bar (absolute x in the strip body). `progress` = work
 *  done (solid), `remaining` = forecast work (hatched), `hold` = the
 *  out-of-sequence pause bridging progress→remaining (dim hatch), `plain` = a
 *  not-started forecast bar. */
type BarSeg = { left: number; right: number; kind: "progress" | "remaining" | "hold" | "plain" };

/** How a row draws: a section `bar` or a `milestone` diamond. */
interface BarRender {
  render: "bar" | "milestone";
  segments: BarSeg[];
  /** milestone diamond x; 0 otherwise. */
  pointX: number;
}

/** A driver (external / finished work) drawn as a read-only mini-bar INSIDE an
 *  activity's row — a `pred` in the row's top strip, a `succ` in the bottom —
 *  at its driving date. No row of its own. */
interface DriverItem {
  key: string;
  role: "pred" | "succ";
  driver: DepGraphActivity;
  edge: RowDriver["edge"];
  box: ActivityBox;
  /** Label text + the relevant (available/needed) date for the tooltip. */
  label: string;
  date: string | null;
}

/** Calendar column for a start date. Exact workday → its index. A date that
 *  isn't a workday column snaps to the prior workday (so a holiday-dated bar
 *  lands at its real spot, not column 0); before the window → 0, after → last.
 *  null date → -1 (caller renders at column 0). `calendar` is ascending ISO. */
export function calendarIndexFor(calendar: string[], iso: string | null): number {
  if (!iso) return -1;
  const exact = calendar.indexOf(iso);
  if (exact >= 0) return exact;
  const next = calendar.findIndex((d) => d >= iso);
  if (next === -1) return calendar.length - 1;
  if (next === 0) return 0;
  return next - 1;
}

/** Calendar column whose LEFT edge is the data-date line: the first column on or
 *  after the data date. Before the window → 0; after it → past the last column
 *  (line sits at the right edge). -1 when there's no data date. */
export function dataDateColumn(calendar: string[], iso: string | null): number {
  if (!iso || calendar.length === 0) return -1;
  const idx = calendar.findIndex((d) => d >= iso);
  return idx === -1 ? calendar.length : idx;
}

interface Props {
  rowLayout: RowLayoutEntry[];
  /** Measured table row heights (one entry per rowLayout entry, same order).
   *  Empty array = pre-measurement; we fall back to ROW_H per row. */
  rowHeights: number[];
  calendar: string[];
  graph: DepGraph;
  bodyRef: React.RefObject<HTMLDivElement | null>;
  selectedId: string | null;
  /** Direct predecessors / successors of the selected row — their rows tint
   *  light red / light yellow (mirrors the table). */
  predIds?: Set<string>;
  succIds?: Set<string>;
  /** Select an activity by clicking its row / bar — toggles like the table. */
  onSelect: (id: string) => void;
  /** Draw only the binding (driving) dependency lines; hide slack preds. */
  drivingOnly: boolean;
}

const DepGanttStrip: React.FC<Props> = ({
  rowLayout,
  rowHeights,
  calendar,
  graph,
  bodyRef,
  selectedId,
  predIds,
  succIds,
  onSelect,
  drivingOnly,
}) => {
  const bodyWidth = calendar.length * DAY_PX;

  const { rowTops, totalHeight } = React.useMemo(() => {
    const tops: number[] = new Array(rowLayout.length);
    let acc = 0;
    for (let i = 0; i < rowLayout.length; i++) {
      tops[i] = acc;
      const h = rowHeights[i] ?? ROW_H;
      acc += h > 0 ? h : ROW_H;
    }
    return { rowTops: tops, totalHeight: acc };
  }, [rowLayout.length, rowHeights]);

  const heightFor = React.useCallback(
    (rowIndex: number) => (rowHeights[rowIndex] ?? ROW_H) || ROW_H,
    [rowHeights],
  );

  // The data-date "now" line column (left edge = the data date).
  const dataDateX = React.useMemo(() => {
    const idx = dataDateColumn(calendar, graph.data_date);
    return idx >= 0 ? idx * DAY_PX : -1;
  }, [calendar, graph.data_date]);

  const { boxesByActivity, activityBars, driverItems } = React.useMemo(() => {
    const byAct = new Map<string, ActivityBox[]>();
    const actBars: Array<{
      entry: Extract<RowLayoutEntry, { kind: "activity" }>;
      box: ActivityBox;
      bar: BarRender;
    }> = [];
    const drivers: DriverItem[] = [];
    const cLeft = (iso: string | null) => {
      const i = calendarIndexFor(calendar, iso);
      return i >= 0 ? i * DAY_PX : null;
    };
    const cRight = (iso: string | null) => {
      const i = calendarIndexFor(calendar, iso);
      return i >= 0 ? (i + 1) * DAY_PX : null;
    };
    const addBox = (id: string, box: ActivityBox) => {
      const list = byAct.get(id);
      if (list) list.push(box);
      else byAct.set(id, [box]);
    };

    // A driver mini-bar INSIDE `act`'s row at `stripTop`, at the driver's own
    // date window, registered (by driver id) so its dependency arrow resolves.
    const buildDriver = (
      act: DepGraphActivity,
      rd: RowDriver,
      rowIndex: number,
      stripTop: number,
    ) => {
      const d = rd.driver;
      const win = driverWindow(d, graph.data_date);
      const sX = cLeft(win.start) ?? cLeft(d.start);
      if (sX === null) return; // no resolvable date → skip marker + its arrow
      const fRaw = cRight(win.finish) ?? cRight(d.finish);
      const fX = fRaw != null && fRaw > sX ? fRaw : sX + DAY_PX;
      const box: ActivityBox = {
        rowIndex,
        left: sX,
        right: fX,
        top: stripTop,
        bottom: stripTop + DRIVER_H,
        midY: stripTop + DRIVER_H / 2,
        driveStartX: sX,
        driveFinishX: fX,
        zoneKey: null,
      };
      const date = driverDate(rd.role, rd.edge.type, win);
      drivers.push({
        key: `${rd.role === "pred" ? "dp" : "ds"}-${act.id}-${d.id}-${rd.edge.edge_id ?? "n"}`,
        role: rd.role,
        driver: d,
        edge: rd.edge,
        box,
        label: `${activityLabel(d)} · ${fmtMonthDay(date)}`,
        date,
      });
      addBox(d.id, box);
    };

    for (const entry of rowLayout) {
      if (entry.kind !== "activity") continue;
      const act = entry.activity;
      const isMilestone = (act.task_type || "").toUpperCase().endsWith("MILE");
      const status = nodeStatus(act);

      const aStartX = cLeft(act.actual_start);
      const dStartX = cLeft(act.drive_start) ?? cLeft(act.start) ?? 0;
      const dFinishRaw = cRight(act.drive_finish) ?? cRight(act.finish);
      const dFinishX = dFinishRaw != null && dFinishRaw > dStartX ? dFinishRaw : dStartX + DAY_PX;

      let segments: BarSeg[] = [];
      let envLeft: number;
      let envRight: number;
      let driveStartX = dStartX;
      let driveFinishX = dFinishX;
      let render: BarRender["render"] = "bar";
      let pointX = 0;

      if (isMilestone) {
        render = "milestone";
        pointX = dStartX + DAY_PX;
        envLeft = pointX - DAY_PX;
        envRight = pointX;
        driveStartX = driveFinishX = pointX;
      } else if (status === "active" && aStartX != null && dataDateX >= 0) {
        const progRight = Math.min(Math.max(dataDateX, aStartX), dFinishX);
        if (progRight > aStartX) segments.push({ left: aStartX, right: progRight, kind: "progress" });
        if (dStartX > progRight + 0.5) segments.push({ left: progRight, right: dStartX, kind: "hold" });
        segments.push({ left: dStartX, right: dFinishX, kind: "remaining" });
        envLeft = Math.min(aStartX, dStartX);
        envRight = dFinishX;
      } else if (status === "complete") {
        // A completed activity row (only the anchors view puts one on the
        // canvas) draws solid at its table (actual) dates — its retained drive
        // point is where it DRIVES from, not where the work happened.
        const sX = cLeft(act.start) ?? dStartX;
        const fRaw = cRight(act.finish);
        const fX = fRaw != null && fRaw > sX ? fRaw : sX + DAY_PX;
        segments = [{ left: sX, right: fX, kind: "progress" }];
        envLeft = sX;
        envRight = fX;
        driveStartX = sX;
        driveFinishX = fX;
      } else {
        segments = [{ left: dStartX, right: dFinishX, kind: status === "active" ? "remaining" : "plain" }];
        envLeft = dStartX;
        envRight = dFinishX;
      }

      // Row bands: each pred driver gets its own lane in a TOP strip, each succ
      // driver its own lane in a BOTTOM strip (so multiples stack, never
      // overlap), and the bar centers in the band between them.
      const rowH = heightFor(entry.rowIndex);
      const rowTop = rowTops[entry.rowIndex];
      const topBand = entry.predDrivers.length * DRIVER_STRIP_H;
      const botBand = entry.succDrivers.length * DRIVER_STRIP_H;
      const innerTop = rowTop + topBand;
      const innerBottom = rowTop + rowH - botBand;
      const top = innerTop + Math.max(0, (innerBottom - innerTop - BAR_H) / 2);
      const box: ActivityBox = {
        rowIndex: entry.rowIndex,
        left: envLeft,
        right: envRight,
        top,
        bottom: top + BAR_H,
        midY: top + BAR_H / 2,
        driveStartX,
        driveFinishX,
        zoneKey: entry.zoneKey,
        anchorExternalId: entry.partnerOf,
        anchorRole: entry.partnerRole,
      };
      actBars.push({ entry, box, bar: { render, segments, pointX } });
      addBox(act.id, box);

      entry.predDrivers.forEach((rd, i) =>
        buildDriver(act, rd, entry.rowIndex, rowTop + 2 + i * DRIVER_STRIP_H),
      );
      entry.succDrivers.forEach((rd, j) =>
        buildDriver(act, rd, entry.rowIndex, rowTop + rowH - botBand + 2 + j * DRIVER_STRIP_H),
      );
    }
    return { boxesByActivity: byAct, activityBars: actBars, driverItems: drivers };
  }, [rowLayout, calendar, rowTops, heightFor, dataDateX, graph.data_date]);

  const arrows = React.useMemo(
    () => computeArrows(graph, boxesByActivity),
    [graph, boxesByActivity],
  );

  // Open the strip at the FIRST activity (the earliest bar), not at the data date
  // — on a plan whose work starts after the data date, opening on the data date
  // left you staring at empty pre-roll. The data date stays reachable by
  // scrolling. Waits for the bars to be laid out before locking.
  const didInitialScrollRef = React.useRef(false);
  React.useLayoutEffect(() => {
    const container = bodyRef && "current" in bodyRef ? bodyRef.current : null;
    if (!container || calendar.length === 0 || didInitialScrollRef.current) return;
    // Open at the section's earliest OWN (in-scope) bar — not the earliest bar
    // overall. Completed / external drivers can sit far in the past (a section
    // like "Corridor Finishes" feeds off rough-in from months ago); opening on
    // them would land the chart on old work with the section's bars off-screen.
    let inScopeMin = Infinity;
    let anyMin = Infinity;
    for (const [id, list] of Array.from(boxesByActivity.entries())) {
      const inScope = graph.activities[id]?.in_scope ?? true;
      for (const b of list) {
        anyMin = Math.min(anyMin, b.left);
        if (inScope) inScopeMin = Math.min(inScopeMin, b.left);
      }
    }
    const open = Number.isFinite(inScopeMin) ? inScopeMin : anyMin;
    if (!Number.isFinite(open)) return; // bars not laid out yet — try next render
    container.scrollLeft = Math.max(0, open - DAY_PX * 3);
    didInitialScrollRef.current = true;
  }, [calendar, boxesByActivity, bodyRef, graph]);

  // Recenter the chart on a USER selection — but only when the selection
  // actually CHANGES (not when a re-measure re-runs this), and never for the
  // automatic first selection that fires when the editor opens (it would yank
  // the chart away from its opening position right after load).
  const scrolledSelRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!selectedId || selectedId === scrolledSelRef.current) return;
    const firstAutoSelect = scrolledSelRef.current === null;
    scrolledSelRef.current = selectedId;
    if (firstAutoSelect) return;
    const container = bodyRef && "current" in bodyRef ? bodyRef.current : null;
    const box = boxesByActivity.get(selectedId)?.[0];
    if (!container || !box) return;
    const target = box.left - (container.clientWidth - (box.right - box.left)) / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [selectedId, boxesByActivity, bodyRef]);

  return (
    <div className="dep-strip">
      <div className="dep-strip-scroll" ref={bodyRef}>
        <div className="dep-strip-header" style={{ width: bodyWidth }}>
          {calendar.map((iso, i) => {
            const d = new Date(iso + "T00:00:00");
            const dateLabel = d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
            const isMonday = d.getDay() === 1;
            const year = d.getFullYear();
            const yearStart = i === 0 || year !== new Date(calendar[i - 1] + "T00:00:00").getFullYear();
            return (
              <div
                key={iso}
                className={`dep-strip-day${isMonday && i > 0 ? " week-start" : ""}`}
                style={{ width: DAY_PX }}
              >
                {/* The year slot is ALWAYS present (blank off-boundary) so every
                    date sits on the same row; the year text at a boundary
                    overflows left-aligned into the blank slots to its right. */}
                <div className="dep-strip-day-dow dep-strip-day-dow--year">
                  {yearStart ? year : " "}
                </div>
                <div className="dep-strip-day-date">{dateLabel}</div>
              </div>
            );
          })}
        </div>
        <div className="dep-strip-body" style={{ width: bodyWidth, height: totalHeight }}>
          {calendar.map((iso, i) =>
            i === 0 ? null : (
              <div key={`gd-${iso}`} className="dep-strip-day-line" style={{ left: i * DAY_PX }} />
            ),
          )}
          {calendar.map((iso, i) => {
            const d = new Date(iso + "T00:00:00");
            const isMonday = d.getDay() === 1;
            if (!isMonday || i === 0) return null;
            return <div key={`gw-${iso}`} className="dep-strip-week-line" style={{ left: i * DAY_PX }} />;
          })}
          {calendar.map((iso, i) => {
            if (i === 0) return null;
            const year = new Date(iso + "T00:00:00").getFullYear();
            if (year === new Date(calendar[i - 1] + "T00:00:00").getFullYear()) return null;
            return <div key={`gy-${iso}`} className="dep-strip-year-line" style={{ left: i * DAY_PX }} />;
          })}
          {dataDateX >= 0 && (
            <div
              className="dep-strip-data-date-line"
              style={{ left: dataDateX, height: totalHeight }}
              title={graph.data_date ? `Data date · ${graph.data_date}` : "Data date"}
            >
              <span className="dep-strip-data-date-label">data date</span>
            </div>
          )}
          {rowLayout.map((entry) => {
            const id = entry.kind === "activity" ? entry.activity.id : null;
            const role =
              id && id === selectedId ? " dep-strip-row--selected"
              : id && predIds?.has(id) ? " dep-strip-row--pred"
              : id && succIds?.has(id) ? " dep-strip-row--succ"
              : "";
            return (
              <div
                key={`r-${entry.key}`}
                className={`dep-strip-row${entry.kind === "header" ? " dep-strip-row--header" : ""}${role}${id ? " dep-strip-row--clickable" : ""}`}
                style={{ top: rowTops[entry.rowIndex], height: heightFor(entry.rowIndex) }}
                onClick={id ? () => onSelect(id) : undefined}
              />
            );
          })}
          {activityBars.map(({ entry, box, bar }) => {
            const act = entry.activity;
            const isSelected = act.id === selectedId;
            const status = nodeStatus(act);

            // Constraint chip — compares the constraint date to the DISPLAYED
            // start/finish (what the user sees in the table), so "driving" reads
            // the same on every surface. Sits just before the bar/milestone;
            // flips to after it when there isn't room on the left.
            const cstr = constraintInfo(act.cstr_type, act.cstr_date, act.start, act.finish);
            const cstrAnchor = bar.render === "milestone" ? bar.pointX : box.left;
            const cstrAfter = cstrAnchor < CSTR_CHIP_ROOM;
            const cstrChip = cstr ? (
              <span
                className={`dep-strip-bar-cstr-chip${cstr.driving ? " is-driving" : ""}${cstrAfter ? " dep-strip-bar-cstr-chip--after" : ""}`}
                style={{
                  left: cstrAfter && bar.render !== "milestone" ? box.right : cstrAnchor,
                  top: box.top,
                  height: BAR_H,
                }}
                title={`${cstr.label} ${cstr.date}${cstr.driving ? " — driving this date (not a dependency)" : ""}`}
              >
                <span className="dep-strip-bar-cstr-icon" aria-hidden="true">⚲</span>
                {cstr.short} {formatDateMonthDay(cstr.date)}
              </span>
            ) : null;

            if (bar.render === "milestone") {
              return (
                <React.Fragment key={`b-${entry.key}`}>
                  <Tooltip content={`${act.task_code} · ${act.name} · ${act.start ?? "—"} (milestone)`}>
                    <div
                      className={`dep-strip-milestone${isSelected ? " dep-strip-milestone--selected" : ""}`}
                      style={{ left: bar.pointX, top: box.top, height: BAR_H }}
                      onClick={() => onSelect(act.id)}
                    >
                      ◆
                    </div>
                  </Tooltip>
                  {cstrChip}
                </React.Fragment>
              );
            }

            const lvl = act.zone?.c ?? "";
            // Gantt bar labels always show the FULL activity name (never the
            // short Takt board label) — if it doesn't fit, it overflows to the
            // right rather than abbreviating.
            const narrow = !fitsInBar(`${act.work_code ?? ""} ${act.name} ${lvl}`, box.right - box.left);
            const baseTip =
              status === "active"
                ? `${act.task_code} · ${act.name}\nstarted ${act.actual_start ?? "—"} · remaining ${act.drive_start ?? "—"} → ${act.finish ?? "—"}`
                : `${act.task_code} · ${act.name} · ${act.start ?? "—"} → ${act.finish ?? "—"}`;
            const tip = cstr
              ? `${baseTip}\n${cstr.label} ${cstr.date}${cstr.driving ? " — driving this date" : ""}`
              : baseTip;
            const envLeft = box.left;
            return (
              <React.Fragment key={`b-${entry.key}`}>
                <Tooltip content={tip}>
                  <div
                    className={`dep-strip-bar${isSelected ? " dep-strip-bar--selected" : ""}`}
                    style={{
                      left: envLeft,
                      width: box.right - box.left,
                      top: box.top,
                      height: BAR_H,
                      color: act.color ? autoFontColor(act.color) : undefined,
                    }}
                    onClick={() => onSelect(act.id)}
                  >
                    {bar.segments.map((seg, i) => (
                      <span
                        key={i}
                        className={`dep-strip-seg dep-strip-seg--${seg.kind}`}
                        style={{ left: seg.left - envLeft, width: Math.max(3, seg.right - seg.left), backgroundColor: act.color || undefined }}
                        aria-hidden="true"
                      />
                    ))}
                    {!narrow && <span className="dep-strip-bar-code">{act.work_code}</span>}
                    {!narrow && <span className="dep-strip-bar-name">{act.name}</span>}
                    {!narrow && lvl && <span className="dep-strip-bar-lvl">{lvl}</span>}
                  </div>
                </Tooltip>
                {cstrChip}
                {narrow && (
                  <span className="dep-strip-bar-extlabel" style={{ left: box.right, top: box.top, height: BAR_H }}>
                    {act.work_code && <span className="dep-strip-bar-extlabel-code">{act.work_code}</span>}
                    <span className="dep-strip-bar-extlabel-name">{activityLabel(act)}</span>
                    {lvl && <span className="dep-strip-bar-lvl">{lvl}</span>}
                  </span>
                )}
              </React.Fragment>
            );
          })}
          {driverItems.map((item) => {
            const d = item.driver;
            const done = d.complete;
            const isSel = d.id === selectedId;
            const labelInside = fitsInBar(`✓ ${item.label}`, item.box.right - item.box.left);
            const origin = driverDescriptor(d).origin;
            return (
              <React.Fragment key={`b-${item.key}`}>
                <Tooltip
                  content={`${item.role === "pred" ? "Driven by" : "Feeds"} · ${origin}\n${item.edge.type} · ${d.name} (${d.task_code})\n${item.role === "pred" ? "available" : "needed"} ${fmtMonthDay(item.date)}`}
                >
                  <div
                    className={`dep-strip-dvrbar${done ? " dep-strip-dvrbar--done" : " dep-strip-dvrbar--external"}${isSel ? " dep-strip-dvrbar--selected" : ""}`}
                    style={{ left: item.box.left, width: item.box.right - item.box.left, top: item.box.top, height: DRIVER_H }}
                    onClick={() => onSelect(d.id)}
                  >
                    <span className="dep-strip-dvrbar-mark" aria-hidden="true">{done ? "✓" : "◦"}</span>
                    {labelInside && <span className="dep-strip-dvrbar-label">{item.label}</span>}
                  </div>
                </Tooltip>
                {!labelInside && (
                  <span
                    className="dep-strip-bar-extlabel dep-strip-bar-extlabel--driver"
                    style={{ left: item.box.right, top: item.box.top, height: DRIVER_H }}
                  >
                    <span className="dep-strip-bar-extlabel-name">{item.label}</span>
                  </span>
                )}
              </React.Fragment>
            );
          })}
          <DepArrows arrows={arrows} width={bodyWidth} height={totalHeight} selectedId={selectedId} drivingOnly={drivingOnly} />
        </div>
      </div>
    </div>
  );
};

export default DepGanttStrip;
